import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Header from '../components/layout/Header';
import PageWrapper from '../components/layout/PageWrapper';
import { checkAggregatedDashboardAccess, getUserAtsType, getAvailableOrganizations } from '../api/aggregatedDashboard';
import { getEmbeddableToken } from '../api/embeddable';
import Loader from '../UI/Loader';
import NotFound from './NotFound';

const NoResultText = styled.p`
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.33;
    letter-spacing: 0.96px;
    text-align: center;
    color: #061c2e;
    padding: 1rem;
`;

interface DashboardState {
    hasAccess: boolean;
    loading: boolean;
    error: string | null;
    atsType: string | undefined;
    token: string | null;
}

// Utility to detect environment
const detectEnvironment = (): string => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return 'local';
    if (hostname.includes('dev') || hostname.includes('staging')) return 'dev';
    return 'default';
};

const AggregatedDashboard: React.FC = () => {
    const [t] = useTranslation();
    const [state, setState] = useState<DashboardState>({
        hasAccess: false,
        loading: true,
        error: null,
        atsType: 'smartrecruiters',
        token: null
    });

    const embeddableId = "f88b8dd9-136c-4335-93dc-655e1e6da0a0";
    const environment = detectEnvironment();

    useEffect(() => {
        const initializeDashboard = async () => {
            try {
                setState(prev => ({ ...prev, loading: true, error: null }));

                // Check if user has access to aggregated dashboard
                const hasAccess = await checkAggregatedDashboardAccess();

                if (!hasAccess) {
                    setState(prev => ({
                        ...prev,
                        loading: false,
                        hasAccess: false,
                        error: 'Access denied. You need admin privileges to view aggregated dashboards.'
                    }));
                    return;
                }

                // Get the ATS type assigned to this user from backend
                const atsResponse = await getUserAtsType();

                console.log('[AggregatedDashboard] Full ATS response:', atsResponse);
                console.log('[AggregatedDashboard] ATS response data:', atsResponse.data);
                console.log('[AggregatedDashboard] ATS response status:', atsResponse.status);
                console.log('[AggregatedDashboard] ATS response headers:', atsResponse.headers);

                const atsType = atsResponse.data.ats_type;

                console.log('[AggregatedDashboard] Extracted atsType:', atsType, 'type:', typeof atsType);

                // Debug: Check what organizations are available for this ATS type
                try {
                    const orgsResponse = await getAvailableOrganizations(atsType);
                    console.log('[AggregatedDashboard] Available organizations:', orgsResponse.data);
                } catch (orgError) {
                    console.warn('[AggregatedDashboard] Could not fetch organizations:', orgError);
                }

                // Get embeddable token
                const securityContext = {
                    atsFilter: atsType,
                    dashboardType: 'aggregated',
                    isAggregatedAdmin: true
                };

                console.log('[AggregatedDashboard] Security context being sent:', securityContext);

                const tokenResponse = await getEmbeddableToken(embeddableId, environment, securityContext);
                const token = tokenResponse.data.token;

                console.log('[AggregatedDashboard] Token received, environment:', environment);

                setState(prev => ({
                    ...prev,
                    loading: false,
                    hasAccess: true,
                    atsType,
                    token
                }));

            } catch (error: any) {
                console.error('Error loading aggregated dashboard:', error);

                let errorMessage = 'Failed to load dashboard. Please try again.';
                if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response?.data?.detail) {
                    errorMessage = error.response.data.detail;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                setState(prev => ({
                    ...prev,
                    loading: false,
                    error: errorMessage
                }));
            }
        };

        initializeDashboard();
    }, [embeddableId, environment]);

    // Load embeddable script
    React.useEffect(() => {
        if (!document.getElementById('embeddable-script')) {
            const script = document.createElement('script');
            script.type = 'module';
            script.src = 'https://api.eu.embeddable.com/js/v1/';
            script.id = 'embeddable-script';
            document.body.appendChild(script);
        }
    }, []);

    // Render the embedded dashboard
    const embeddableRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (state.token && embeddableRef.current) {
            const clientContext = {
                theme: state.atsType || 'smartrecruiters',
                atsFilter: state.atsType || 'smartrecruiters',
                dashboardType: 'aggregated'
            };

            console.log('[AggregatedDashboard] Rendering embeddable with:', {
                token: state.token?.substring(0, 20) + '...',
                clientContext,
                atsType: state.atsType,
                environment
            });

            embeddableRef.current.innerHTML = '';
            const el = document.createElement('em-beddable');
            el.setAttribute('token', state.token);
            el.setAttribute('client-context', JSON.stringify(clientContext));
            embeddableRef.current.appendChild(el);
        }
    }, [state.token, state.atsType, environment]);

    // Handle access denied
    if (!state.hasAccess && !state.loading && state.error) {
        return <NotFound message={state.error} />;
    }

    return (
        <PageWrapper>
            <Header title={`${t('Aggregated Dashboard')}${state.atsType ? ` - ${state.atsType.charAt(0).toUpperCase() + state.atsType.slice(1)}` : ''}`} />
            <Loader spinning={state.loading}>
                {state.error && !state.hasAccess ? (
                    <NoResultText>{state.error}</NoResultText>
                ) : state.token ? (
                    <div style={{ height: "calc(100vh - 64px - 3rem - 40px)" }}>
                        <div ref={embeddableRef} style={{ height: '100%' }} />
                    </div>
                ) : null}
            </Loader>
        </PageWrapper>
    );
};

export default AggregatedDashboard; 